import type { BlockType } from '@/lib/workflow';
import styles from './BlockSkeleton.module.css';

/**
 * A block-shaped placeholder.
 *
 * Shape-aware rather than a single spinner: a skeleton that already looks like
 * a chart, a stat row or a table tells you what is arriving and reserves the
 * space it will occupy, so nothing jumps when the data lands. A spinner tells
 * you only that something is happening, and then the layout moves anyway.
 */

export function BlockSkeleton({ type = 'chart' }: { type?: BlockType }) {
  if (type === 'banner') {
    return (
      <div className={styles.banner} aria-hidden="true">
        <span className={styles.poster} />
        <span className={styles.bannerText}>
          <span className={styles.line} data-w="42" data-h="tall" />
          <span className={styles.line} data-w="60" />
          <span className={styles.line} data-w="48" />
        </span>
      </div>
    );
  }

  if (type === 'kpis') {
    return (
      <div className={styles.kpis} aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className={styles.kpi}>
            <span className={styles.line} data-w="55" data-h="short" />
            <span className={styles.line} data-w="80" data-h="tall" />
            <span className={styles.line} data-w="40" data-h="short" />
          </span>
        ))}
      </div>
    );
  }

  if (type === 'table') {
    return (
      <div className={styles.table} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className={styles.row} style={{ opacity: 1 - i * 0.13 }}>
            <span className={styles.line} data-w="34" />
            <span className={styles.line} data-w="18" />
            <span className={styles.line} data-w="14" />
          </span>
        ))}
      </div>
    );
  }

  if (type === 'text' || type === 'note') {
    return (
      <div className={styles.prose} aria-hidden="true">
        <span className={styles.line} data-w="96" />
        <span className={styles.line} data-w="88" />
        <span className={styles.line} data-w="54" />
      </div>
    );
  }

  /* Charts: bars of varying height, which reads as a chart at a glance far
     faster than a grey rectangle does. */
  return (
    <div className={styles.chart} aria-hidden="true">
      {[58, 82, 44, 96, 66, 74, 38].map((h, i) => (
        <span key={i} className={styles.bar} style={{ height: `${h}%` }} />
      ))}
    </div>
  );
}

/**
 * The whole document, before the agent has returned anything.
 *
 * Shaped like a plausible deliverable — cover, headline figures, a pair of
 * charts — because the agent almost always builds one, and a placeholder that
 * matches what arrives makes the transition feel like completion rather than
 * replacement.
 */
export function DocumentSkeleton({ label }: { label: string }) {
  const plan: { type: BlockType; layout: 'full' | 'half' }[] = [
    { type: 'banner', layout: 'full' },
    { type: 'kpis', layout: 'full' },
    { type: 'chart', layout: 'half' },
    { type: 'chart', layout: 'half' },
  ];

  return (
    <div className={styles.doc}>
      <p className={styles.status} role="status">
        <span className={styles.pulse} aria-hidden="true" />
        {label}
      </p>
      <div className={styles.docGrid}>
        {plan.map((item, i) => (
          <div
            key={i}
            className={styles.ghost}
            data-layout={item.layout}
            /* Staggered so the page assembles rather than flashing in — the
               same rhythm the real blocks use when they mount. */
            style={{ animationDelay: `${i * 90}ms` }}
          >
            <span className={styles.ghostHead}>
              <span className={styles.line} data-w="30" />
            </span>
            <BlockSkeleton type={item.type} />
          </div>
        ))}
      </div>
    </div>
  );
}
